import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';
type Language = 'es' | 'en';
type CalendarView = 'day' | 'week' | 'month' | 'schedule' | 'uniforms';

interface UIState {
  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // Language
  language: Language;
  setLanguage: (language: Language) => void;

  // Sidebar
  sidebarCollapsed: boolean;
  sidebarOpen: boolean; // For mobile
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Calendar view
  calendarView: CalendarView;
  setCalendarView: (view: CalendarView) => void;

  // Selected date
  selectedDate: string;
  setSelectedDate: (date: string) => void;

  // Selected student filter (null = all)
  selectedStudentId: string | null;
  setSelectedStudentId: (id: string | null) => void;

  // Command palette
  commandOpen: boolean;
  setCommandOpen: (open: boolean) => void;

  // Create task modal
  createTaskOpen: boolean;
  setCreateTaskOpen: (open: boolean) => void;

  // Create student modal
  createStudentOpen: boolean;
  setCreateStudentOpen: (open: boolean) => void;

  // Create event modal
  createEventOpen: boolean;
  setCreateEventOpen: (open: boolean) => void;

  // Upgrade dialog
  upgradeDialogOpen: boolean;
  setUpgradeDialogOpen: (open: boolean) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),

      // Language
      language: 'es',
      setLanguage: (language) => set({ language }),

      // Sidebar
      sidebarCollapsed: false,
      sidebarOpen: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),

      // Calendar view
      calendarView: 'week',
      setCalendarView: (calendarView) => set({ calendarView }),

      // Selected date (use local date to avoid timezone issues)
      selectedDate: (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      })(),
      setSelectedDate: (selectedDate) => set({ selectedDate }),

      // Selected student filter
      selectedStudentId: null,
      setSelectedStudentId: (selectedStudentId) => set({ selectedStudentId }),

      // Command palette
      commandOpen: false,
      setCommandOpen: (commandOpen) => set({ commandOpen }),

      // Create task modal
      createTaskOpen: false,
      setCreateTaskOpen: (createTaskOpen) => set({ createTaskOpen }),

      // Create student modal
      createStudentOpen: false,
      setCreateStudentOpen: (createStudentOpen) => set({ createStudentOpen }),

      // Create event modal
      createEventOpen: false,
      setCreateEventOpen: (createEventOpen) => set({ createEventOpen }),

      // Upgrade dialog
      upgradeDialogOpen: false,
      setUpgradeDialogOpen: (upgradeDialogOpen) => set({ upgradeDialogOpen }),
    }),
    {
      name: 'frappi-ui-storage',
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
        sidebarCollapsed: state.sidebarCollapsed,
        calendarView: state.calendarView,
      }),
    }
  )
);
